import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { useTokenLocks } from '../../hooks/useTokenLocks';

import { PriceDiscoveryChart } from './PriceDiscoveryChart';
import { RecentDeployments } from './RecentDeployments';
import { SystemArchitecture } from './SystemArchitecture';
import { useAccount, useChainId } from 'wagmi';
import { useDisconnect } from 'wagmi';
import WalletConnect from '../WalletConnect';
import { ZNSAddressDisplay } from '../shared/ZNSAddressDisplay';
import { ZNSRegistrationModal } from '../shared/ZNSRegistrationModal';
import { SUPPORTED_CHAINS } from '../../utils/relayProtocol';


const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { disconnect } = useDisconnect();
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [marketStats, setMarketStats] = useState<{ total: number; protofire: number; inkyswap: number } | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showZNSModal, setShowZNSModal] = useState(false);
  const problemsSectionRef = useRef<HTMLElement>(null);
  const solutionsSectionRef = useRef<HTMLElement>(null);
  const featuresSectionRef = useRef<HTMLElement>(null);
  const statsSectionRef = useRef<HTMLElement>(null);
  const locksSectionRef = useRef<HTMLElement>(null);
  const swapSectionRef = useRef<HTMLElement>(null);

  // Get TVL data from the existing hook
  const { totalTVL, locks } = useTokenLocks();

  // Function to get current chain logo
  const getCurrentChainLogo = () => {
    const chain = Object.values(SUPPORTED_CHAINS).find(chain => chain.chainId === chainId);
    return chain?.logo || SUPPORTED_CHAINS.INK.logo;
  };

  const handleMobileNavClick = () => {
    setMobileMenuOpen(false);
  };

  const handleOpenApp = () => {
    navigate('/explore');
  };

  // Track visitor on main landing page
  useEffect(() => {
    // Track visitor by calling the analytics API
    // This ensures all visitors to the main site are tracked
    fetch('/api/analytics/visitors', {
      method: 'GET',
    }).catch((error) => {
      // Silently fail - tracking shouldn't break the user experience
      console.debug('Visitor tracking failed (non-critical):', error);
    });
  }, []);

  // Fetch token count from Supabase
  useEffect(() => {
    const fetchTokenCount = async () => {
      try {
        // Get total count from tokens table (all Ink network tokens)
        const totalResult = await supabase
          .from('tokens')
          .select('id', { count: 'exact' })
          .eq('network', 'ink');

        // Get breakdown by DEX
        const [protofireResult, inkyswapResult] = await Promise.all([
          // Protofire: dex is null or dex = 'Protofire'
          supabase
            .from('tokens')
            .select('id', { count: 'exact' })
            .eq('network', 'ink')
            .or('dex.is.null,dex.eq.Protofire'),
          // InkySwap: dex = 'InkySwap'
          supabase
            .from('tokens')
            .select('id', { count: 'exact' })
            .eq('network', 'ink')
            .eq('dex', 'InkySwap')
        ]);

        if (!totalResult.error && !protofireResult.error && !inkyswapResult.error) {
          const totalCount = totalResult.count || 0;
          const protofireCount = protofireResult.count || 0;
          const inkyswapCount = inkyswapResult.count || 0;

          console.log('Token count:', { protofire: protofireCount, inkyswap: inkyswapCount, total: totalCount });
          setMarketStats({
            total: totalCount,
            protofire: protofireCount,
            inkyswap: inkyswapCount
          });
        } else {
          console.error('Error fetching token count:', totalResult.error || protofireResult.error || inkyswapResult.error);
        }
      } catch (error) {
        console.error('Error fetching token count:', error);
      }
    };

    fetchTokenCount();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.getAttribute('data-section');
            if (sectionId) {
              setVisibleSections(prev => new Set(prev).add(sectionId));
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observe all sections
    const sections = [
      problemsSectionRef.current,
      solutionsSectionRef.current,
      featuresSectionRef.current,
      statsSectionRef.current,
      locksSectionRef.current,
      swapSectionRef.current
    ];

    sections.forEach(section => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="landing-page-container" style={{
      background: 'transparent',
      color: '#ffffff',
      fontFamily: 'Plus Jakarta Sans, sans-serif',
      overflowX: 'hidden',
      overflowY: 'auto',
      minHeight: '100vh',
      border: 'none',
      outline: 'none',
      boxSizing: 'border-box',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }}>
      {/* Font Face CSS */}
      <style>
        {`
          * {
            border: none !important;
            outline: none !important;
          }
          
          html, body {
            border: none !important;
            outline: none !important;
            overflow-x: hidden !important;
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
          }
          
          html::-webkit-scrollbar, body::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          
          *::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          
          .landing-page-container::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          
          @font-face {
            font-family: 'Plus Jakarta Sans';
            src: url('/landing-assets/PlusJakartaSans-Regular.ttf') format('truetype');
            font-weight: 400;
            font-style: normal;
          }
          
          @font-face {
            font-family: 'Plus Jakarta Sans';
            src: url('/landing-assets/PlusJakartaSans-SemiBold.ttf') format('truetype');
            font-weight: 600;
            font-style: normal;
          }
          
          @font-face {
            font-family: 'Plus Jakarta Sans';
            src: url('/landing-assets/PlusJakartaSans-MediumItalic.ttf') format('truetype');
            font-weight: 500;
            font-style: italic;
          }
          
          @font-face {
            font-family: 'Plus Jakarta Sans';
            src: url('/landing-assets/PlusJakartaSans-Bold.ttf') format('truetype');
            font-weight: 700;
            font-style: normal;
          }

          .landing-container {
            max-width: 1600px;
            margin: 0 auto;
            padding: 0 20px;
          }

          .landing-header {
            padding: 20px 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }

          .landing-header-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

          .landing-logo {
            height: 40px;
            cursor: pointer;
          }

          .landing-nav-links {
            display: flex;
            gap: 4px;
            align-items: center;
            flex-shrink: 1;
          }

          .landing-nav-link {
            padding: 10px 14px;
            background: transparent;
            border: none;
            border-radius: 8px;
            color: #ffffff;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            font-family: 'Plus Jakarta Sans', sans-serif;
            text-decoration: none;
            white-space: nowrap;
          }

          .landing-nav-link:hover {
            background: rgba(88, 28, 135, 0.3);
          }

          .landing-right-section {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-shrink: 0;
          }

          .landing-wallet-badge {
            padding: 10px 16px;
            background: rgba(88, 28, 135, 0.3);
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.3s ease;
            border: none;
            color: #ffffff;
          }

          .landing-wallet-badge:hover {
            background: rgba(88, 28, 135, 0.5);
          }

          .landing-wallet-badge * {
            color: #ffffff !important;
          }

          .landing-chain-logo {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            object-fit: cover;
          }

          .landing-hamburger {
            display: none;
            background: transparent;
            border: none;
            color: #ffffff;
            font-size: 24px;
            cursor: pointer;
            padding: 8px;
            transition: all 0.3s ease;
          }

          .landing-hamburger:hover {
            color: #a855f7;
          }

          .landing-mobile-menu-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            backdrop-filter: blur(4px);
            z-index: 999;
            display: none;
          }

          .landing-mobile-menu-overlay.open {
            display: block;
          }

          .landing-mobile-menu {
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            width: 280px;
            max-width: 85vw;
            background: rgba(18, 18, 26, 0.98);
            backdrop-filter: blur(20px);
            border-left: 1px solid rgba(168, 85, 247, 0.2);
            z-index: 1000;
            transform: translateX(100%);
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            overflow-y: auto;
            display: flex;
            flex-direction: column;
          }

          .landing-mobile-menu.open {
            transform: translateX(0);
          }

          .landing-mobile-menu-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px 20px;
            border-bottom: 1px solid rgba(168, 85, 247, 0.2);
          }

          .landing-mobile-menu-logo {
            height: 24px;
          }

          .landing-mobile-close {
            background: transparent;
            border: none;
            color: #ffffff;
            font-size: 24px;
            cursor: pointer;
            padding: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          }

          .landing-mobile-close:hover {
            color: #a855f7;
          }

          .landing-mobile-nav-links {
            display: flex;
            flex-direction: column;
            padding: 20px;
            gap: 8px;
          }

          .landing-mobile-nav-link {
            padding: 14px 16px;
            background: transparent;
            border: none;
            border-radius: 8px;
            color: #ffffff;
            font-size: 15px;
            font-weight: 500;
            font-family: 'Plus Jakarta Sans', sans-serif;
            cursor: pointer;
            transition: all 0.3s ease;
            text-align: left;
            width: 100%;
          }

          .landing-mobile-nav-link:hover {
            background: rgba(88, 28, 135, 0.3);
            transform: translateX(4px);
          }

          .landing-mobile-wallet-section {
            padding: 20px 20px 100px 20px;
            border-top: 1px solid rgba(168, 85, 247, 0.2);
            margin-top: auto;
          }

          .landing-hero {
            text-align: center;
            padding: 140px 0 100px;
            position: relative;
            min-height: 90vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }

          .landing-hero::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 100vw;
            height: 100vh;
            background: transparent;
            z-index: -1;
            pointer-events: none;
          }

          .landing-hero-content {
            max-width: 900px;
            margin: 0 auto;
            z-index: 1;
          }

          .landing-hero-title {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 800;
            font-size: 4.5rem;
            line-height: 1.1;
            margin-bottom: 24px;
            background: linear-gradient(135deg, #ffffff 0%, #e9d5ff 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            text-fill-color: transparent;
            opacity: 0;
            animation: fadeInUp 1s ease-out 0.5s forwards;
            letter-spacing: -0.02em;
            text-shadow: 0 0 40px rgba(168, 85, 247, 0.2);
          }

          .landing-hero-subtitle {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 400;
            font-size: 1.25rem;
            line-height: 1.6;
            margin-bottom: 40px;
            color: rgba(255, 255, 255, 0.7);
            max-width: 600px;
            margin-left: auto;
            margin-right: auto;
            opacity: 0;
            animation: fadeInUp 1s ease-out 0.7s forwards;
          }

          .landing-hero-tagline {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 600;
            font-size: 1rem;
            color: #a855f7;
            text-transform: uppercase;
            letter-spacing: 2px;
            margin-bottom: 16px;
            opacity: 0;
            animation: fadeInUp 1s ease-out 0.3s forwards;
            display: block;
          }

          .landing-cta-button {
            display: inline-block;
            background: rgba(88, 28, 135, 0.9);
            color: #ffffff;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 600;
            font-size: 1.1rem;
            padding: 16px 48px;
            border-radius: 12px;
            text-decoration: none;
            cursor: pointer;
            transition: all 0.3s ease;
            opacity: 0;
            animation: fadeInUp 1s ease-out 1.1s forwards;
            border: none;
            box-shadow: 0 4px 12px rgba(88, 28, 135, 0.4);
            min-width: 280px;
            text-align: center;
          }

          .landing-cta-button:hover {
            transform: translateY(-2px);
            background: rgba(88, 28, 135, 1);
            box-shadow: 0 6px 20px rgba(88, 28, 135, 0.6);
          }

          .landing-cta-button:active {
            transform: translateY(0);
          }

          .landing-cta-buttons {
            display: flex;
            flex-direction: column;
            gap: 16px;
            align-items: center;
            justify-content: center;
            width: 100%;
            max-width: 400px;
            margin: 0 auto;
            padding: 0 20px;
          }

          .landing-cta-button-secondary {
            display: inline-block;
            background: rgba(88, 28, 135, 0.9);
            color: #ffffff;
            padding: 18px 48px;
            border-radius: 12px;
            text-decoration: none;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 700;
            font-size: 1.35rem;
            cursor: pointer;
            transition: all 0.3s ease;
            border: none;
            box-shadow: 0 0 12px rgba(168, 85, 247, 0.5), 0 0 24px rgba(168, 85, 247, 0.25);
            animation: pulseGlow 1.8s ease-in-out infinite;
            min-width: 280px;
            text-align: center;
          }

          .landing-cta-button-secondary:hover {
            transform: translateY(-2px);
            background: rgba(88, 28, 135, 1);
            box-shadow: 0 0 16px rgba(168, 85, 247, 0.7), 0 0 32px rgba(168, 85, 247, 0.35);
          }

          @keyframes pulseGlow {
            0% {
              box-shadow: 0 0 10px rgba(168, 85, 247, 0.5), 0 0 20px rgba(168, 85, 247, 0.25);
            }
            50% {
              box-shadow: 0 0 20px rgba(168, 85, 247, 0.8), 0 0 40px rgba(168, 85, 247, 0.4);
            }
            100% {
              box-shadow: 0 0 10px rgba(168, 85, 247, 0.5), 0 0 20px rgba(168, 85, 247, 0.25);
            }
          }

          .landing-cta-button-secondary:active {
            transform: translateY(0);
          }



          .landing-section-separator {
            width: 100%;
            height: 1px;
            background-color: rgba(255, 255, 255, 0.1);
            margin: 60px auto;
            max-width: 600px;
          }

          .landing-problems {
            padding: 80px 0;
            background: transparent;
            opacity: 0;
            transform: translateY(30px);
            transition: opacity 0.8s ease, transform 0.8s ease;
          }

          .landing-problems.visible {
            opacity: 1;
            transform: translateY(0);
          }

          .landing-problems-title {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 700;
            font-size: 3rem;
            text-align: center;
            margin-bottom: 80px;
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: unset !important;
            background-clip: unset !important;
            text-fill-color: unset !important;
          }

          .landing-problems-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 60px;
            margin-bottom: 80px;
          }

          .landing-problem-card {
            text-align: center;
            padding: 40px 20px;
            border-radius: 20px;
            background: transparent;
            border: none;
            transition: transform 0.3s ease;
          }

          .landing-problem-card:hover {
            transform: translateY(-5px);
            background: rgba(20, 20, 30, 0.8);
            border-color: var(--color-neon-purple, #bc13fe);
            box-shadow: 0 0 20px rgba(188, 19, 254, 0.15);
          }

          .landing-problem-icon {
            width: 48px;
            height: 48px;
            margin: 15px auto 20px;
            opacity: 0.8;
          }

          .landing-problem-title {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 600;
            font-size: 1.5rem;
            margin-bottom: 20px;
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: unset !important;
            background-clip: unset !important;
            text-fill-color: unset !important;
          }

          .landing-problem-description {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 500;
            font-style: italic;
            font-size: 1.1rem;
            line-height: 1.6;
            color: rgba(255, 255, 255, 0.8);
          }

          .landing-solutions {
            padding: 80px 0;
            background: transparent;
            opacity: 0;
            transform: translateY(30px);
            transition: opacity 0.8s ease, transform 0.8s ease;
          }

          .landing-solutions.visible {
            opacity: 1;
            transform: translateY(0);
          }

          .landing-architecture {
            padding: 80px 0;
            background: transparent;
            opacity: 0;
            transform: translateY(30px);
            transition: opacity 0.8s ease, transform 0.8s ease;
          }

          .landing-architecture.visible {
            opacity: 1;
            transform: translateY(0);
          }

          .landing-architecture-title {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 700;
            font-size: 2.5rem;
            text-align: center;
            margin-bottom: 60px;
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: unset !important;
            background-clip: unset !important;
            text-fill-color: unset !important;
          }

          .landing-features {
            padding: 80px 0;
            background: transparent;
            opacity: 0;
            transform: translateY(30px);
            transition: opacity 0.8s ease, transform 0.8s ease;
          }

          .landing-features.visible {
            opacity: 1;
            transform: translateY(0);
          }

          .landing-features-title {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 700;
            font-size: 3rem;
            text-align: center;
            margin-bottom: 60px;
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: unset !important;
            background-clip: unset !important;
            text-fill-color: unset !important;
          }

          .landing-features-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 40px;
            margin-bottom: 80px;
          }

          .landing-feature-card {
            text-align: center;
            padding: 40px 20px;
            border-radius: 20px;
            background: rgba(20, 20, 30, 0.6);
            border: 1px solid rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(10px);
            transition: all 0.3s ease;
            opacity: 0;
            transform: translateY(20px);
          }

          .landing-feature-card.visible {
            opacity: 1;
            transform: translateY(0);
          }

          .landing-feature-card:hover {
            transform: translateY(-5px);
            background: rgba(20, 20, 30, 0.8);
            border-color: var(--color-neon-purple, #bc13fe);
            box-shadow: 0 0 20px rgba(188, 19, 254, 0.15);
          }

          .landing-feature-icon {
            width: 60px;
            height: 60px;
            margin-bottom: 20px;
            opacity: 0.9;
          }

          .landing-feature-title {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 600;
            font-size: 1.3rem;
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: unset !important;
            background-clip: unset !important;
            text-fill-color: unset !important;
            margin-bottom: 10px;
            min-height: 3.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .landing-feature-description {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 500;
            font-style: italic;
            font-size: 1.1rem;
            line-height: 1.6;
            color: rgba(255, 255, 255, 0.8);
            margin-bottom: 20px;
          }

          .landing-feature-cta {
            color: #a855f7;
            text-decoration: none;
            font-weight: 600;
            font-size: 1rem;
            transition: color 0.3s ease;
          }

          .landing-feature-cta:hover {
            color: #ffffff;
          }

          .landing-stats {
            padding: 80px 0;
            background: transparent;
            opacity: 0;
            transform: translateY(30px);
            transition: opacity 0.8s ease, transform 0.8s ease;
          }

          .landing-stats.visible {
            opacity: 1;
            transform: translateY(0);
          }

          .landing-stats-title {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 700;
            font-size: 3rem;
            text-align: center;
            margin-bottom: 60px;
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: unset !important;
            background-clip: unset !important;
            text-fill-color: unset !important;
          }

          .landing-stats-grid {
            display: grid;
          }

          .landing-stat-card.visible {
            opacity: 1;
            transform: translateY(0);
          }

          .landing-stat-card:hover {
            transform: translateY(-5px);
          }

          .landing-stat-number {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 700;
            font-size: 2.5rem;
            color: #a855f7;
            margin-bottom: 10px;
          }

          .landing-stat-label {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 500;
            font-size: 1rem;
            color: rgba(255, 255, 255, 0.8);
          }

          .landing-locks {
            padding: 80px 0;
            background: transparent;
            opacity: 0;
            transform: translateY(30px);
            transition: opacity 0.8s ease, transform 0.8s ease;
          }

          .landing-locks.visible {
            opacity: 1;
            transform: translateY(0);
          }

          .landing-locks-content {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 60px;
            align-items: center;
          }

          .landing-locks-text {
            text-align: left;
          }

          .landing-locks-title {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 700;
            font-size: 2.5rem;
            margin-bottom: 20px;
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: unset !important;
            background-clip: unset !important;
            text-fill-color: unset !important;
          }

          .landing-locks-description {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 400;
            font-size: 1.2rem;
            line-height: 1.6;
            color: rgba(255, 255, 255, 0.8);
            margin-bottom: 30px;
          }

          .landing-locks-features {
            list-style: none;
            padding: 0;
            margin: 0;
          }

          .landing-locks-features li {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 500;
            font-size: 1.1rem;
            color: rgba(255, 255, 255, 0.9);
            margin-bottom: 15px;
            padding-left: 25px;
            position: relative;
          }

          .landing-locks-features li:before {
            content: "✓";
            position: absolute;
            left: 0;
            color: #a855f7;
            font-weight: bold;
          }

          .landing-locks-visual {
            text-align: center;
            padding: 40px;
            border-radius: 20px;
            background: transparent;
            border: none;
          }

          .landing-locks-icon {
            width: 80px;
            height: 80px;
            margin-bottom: 20px;
            opacity: 0.9;
          }

          .landing-swap {
            padding: 80px 0;
            background: transparent;
            opacity: 0;
            transform: translateY(30px);
            transition: opacity 0.8s ease, transform 0.8s ease;
          }

          .landing-swap.visible {
            opacity: 1;
            transform: translateY(0);
          }

          .landing-swap-content {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 60px;
            align-items: center;
          }

          .landing-swap-visual {
            text-align: center;
            padding: 40px;
            border-radius: 20px;
            background: transparent;
            border: none;
          }

          .landing-swap-icon {
            width: 80px;
            height: 80px;
            margin-bottom: 20px;
            opacity: 0.9;
          }

          .landing-swap-text {
            text-align: left;
          }

          .landing-swap-title {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 700;
            font-size: 2.5rem;
            margin-bottom: 20px;
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: unset !important;
            background-clip: unset !important;
            text-fill-color: unset !important;
          }

          .landing-swap-description {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 400;
            font-size: 1.2rem;
            line-height: 1.6;
            color: rgba(255, 255, 255, 0.8);
            margin-bottom: 30px;
          }

          .landing-swap-features {
            list-style: none;
            padding: 0;
            margin: 0;
          }

          .landing-swap-features li {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 500;
            font-size: 1.1rem;
            color: rgba(255, 255, 255, 0.9);
            margin-bottom: 15px;
            padding-left: 25px;
            position: relative;
          }

          .landing-swap-features li:before {
            content: "✓";
            position: absolute;
            left: 0;
            color: #a855f7;
            font-weight: bold;
          }

          .landing-solutions-title {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 700;
            font-size: 3rem;
            text-align: center;
            margin-bottom: 60px;
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: unset !important;
            background-clip: unset !important;
            text-fill-color: unset !important;
          }

          .landing-solutions-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 60px;
            margin-bottom: 80px;
          }

          .landing-solution-card {
            text-align: center;
            padding: 40px 20px;
            border-radius: 20px;
            background: rgba(20, 20, 30, 0.6);
            border: 1px solid rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(10px);
            transition: all 0.3s ease;
          }

          .landing-solution-card:hover {
            transform: translateY(-5px);
            background: rgba(20, 20, 30, 0.8);
            border-color: var(--color-neon-green, #0aff0a);
            box-shadow: 0 0 20px rgba(10, 255, 10, 0.15);
          }

          .landing-solution-icon {
            width: 48px;
            height: 48px;
            margin: 15px auto 20px;
            opacity: 0.8;
            object-fit: contain;
            object-position: center;
          }

          .landing-solution-title {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 600;
            font-size: 1.3rem;
            color: #ffffff !important;
            background: none !important;
            -webkit-background-clip: unset !important;
            -webkit-text-fill-color: unset !important;
            background-clip: unset !important;
            text-fill-color: unset !important;
            margin-bottom: 10px;
            min-height: 3.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .landing-solution-description {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 500;
            font-style: italic;
            font-size: 1.1rem;
            line-height: 1.6;
            color: rgba(255, 255, 255, 0.8);
          }

          .landing-built-on {
            padding: 80px 0;
            text-align: center;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
          }

          .landing-built-on-text {
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-weight: 400;
            font-size: 1.2rem;
            margin-bottom: 40px;
            color: rgba(255, 255, 255, 0.7);
          }

          .landing-built-on-logos {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 15px;
            flex-wrap: nowrap;
            max-width: 100%;
            overflow: hidden;
          }

          .landing-built-on-logos img {
            height: 45px;
            max-width: 140px;
            opacity: 0.7;
            transition: opacity 0.3s ease;
            object-fit: contain;
          }

          .landing-built-on-logos img:hover {
            opacity: 1;
          }

          .landing-plus-icon {
            font-size: 24px;
            color: rgba(255, 255, 255, 0.6);
            margin: 0 10px;
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @media (max-width: 1024px) {
            .landing-problems-grid {
              grid-template-columns: 1fr;
              gap: 40px;
            }
            .landing-solutions-grid {
              grid-template-columns: 1fr;
              gap: 40px;
            }
            .landing-features-grid {
              grid-template-columns: 1fr;
              gap: 30px;
            }
            .landing-stats-grid {
              grid-template-columns: repeat(3, 1fr);
              gap: 30px;
            }
            .landing-locks-content {
              grid-template-columns: 1fr;
              gap: 40px;
            }
            .landing-swap-content {
              grid-template-columns: 1fr;
              gap: 40px;
            }
            .landing-breakthrough-content {
              flex-direction: column;
              gap: 40px;
            }
            .landing-breakthrough-chart, 
            .landing-breakthrough-steps {
              width: 100%;
              min-width: unset !important;
            }
            .landing-promise-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          
          @media (max-width: 768px) {
            .landing-logo {
              height: 32px;
            }

            .landing-nav-links {
              display: none;
            }

            .landing-right-section {
              display: none;
            }

            .landing-hamburger {
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .landing-hero-title {
              font-size: 2.5rem;
            }
            
            .landing-hero-subtitle {
              font-size: 1.2rem;
            }

            .landing-cta-button-secondary {
              font-size: 1.25rem;
              padding: 16px 40px;
              min-width: 260px;
            }

            .landing-cta-button {
              font-size: 1rem;
              padding: 14px 40px;
              min-width: 260px;
            }

            .landing-cta-buttons {
              gap: 14px;
              max-width: 340px;
            }
            
            .landing-problems-title {
              font-size: 2rem;
            }
            
            .landing-architecture-title {
              font-size: 2rem;
            }
            
            .landing-problem-card {
              padding: 30px 15px;
            }
            
            .landing-features-title {
              font-size: 2rem;
            }
            
            .landing-feature-card {
              padding: 30px 20px;
            }
            
            .landing-stats-title {
              font-size: 2rem;
            }
            
            .landing-stats-grid {
              grid-template-columns: 1fr;
              gap: 20px;
            }
            
            .landing-locks-title {
              font-size: 2rem;
            }
            
            .landing-swap-title {
              font-size: 2rem;
            }
            
            .landing-built-on-logos {
              gap: 10px;
            }
            
            .landing-built-on-logos img {
              height: 30px;
              max-width: 90px;
            }
            
            .landing-plus-icon {
              font-size: 18px;
              margin: 0 5px;
            }
          }

          @media (max-width: 480px) {
            .landing-hero {
              padding: 60px 0 80px;
            }
            
            .landing-hero-title {
              font-size: 2rem;
            }
            
            .landing-hero-subtitle {
              font-size: 1rem;
            }

            .landing-cta-button-secondary {
              font-size: 1.15rem;
              padding: 15px 32px;
              min-width: 100%;
              max-width: 300px;
            }

            .landing-cta-button {
              font-size: 0.95rem;
              padding: 13px 32px;
              min-width: 100%;
              max-width: 300px;
            }

            .landing-cta-buttons {
              gap: 12px;
              max-width: 300px;
              padding: 0 15px;
            }
            
            .landing-problems {
              padding: 60px 0;
            }
            
            .landing-built-on-logos {
              gap: 8px;
            }
            
            .landing-plus-icon {
              font-size: 16px;
              margin: 0 3px;
            }
            
            .landing-breakthrough-chart {
               transform: scale(0.8);
               margin-left: -10%;
               width: 120%;
            }
          }



          .landing-stat-tooltip {
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%) translateY(10px);
            background: rgba(20, 20, 30, 0.95);
            border: 1px solid rgba(168, 85, 247, 0.3);
            padding: 12px 16px;
            border-radius: 12px;
            width: 220px;
            opacity: 0;
            visibility: hidden;
            transition: all 0.3s ease;
            pointer-events: none;
            backdrop-filter: blur(10px);
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
            z-index: 100;
            font-size: 0.85rem;
            text-align: left;
          }

          .landing-stat-item:hover .landing-stat-tooltip {
            opacity: 1;
            visibility: visible;
            transform: translateX(-50%) translateY(-10px);
          }

          .landing-stat-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 4px;
            color: rgba(255, 255, 255, 0.8);
          }

          .landing-stat-row:last-child {
            margin-bottom: 0;
          }
        `}
      </style>

      {/* Header */}
      <header className="landing-header">
        <div className="landing-container">
          <div className="landing-header-content">
            <img
              src="/landing-assets/mavrk_word_logo.png"
              alt="Mavrk"
              className="landing-logo"
              onClick={() => navigate('/explore')}
            />
            <div className="landing-nav-links">
              <button onClick={() => navigate('/create')} className="landing-nav-link">Create Token</button>
              <button onClick={() => navigate('/explore')} className="landing-nav-link">Explore</button>
              <button onClick={() => navigate('/bridge')} className="landing-nav-link">Bridge</button>
              <button onClick={() => navigate('/locks')} className="landing-nav-link">Locks & TVL</button>
              <button onClick={() => navigate('/tydro')} className="landing-nav-link">Tydro</button>
              <button onClick={() => navigate('/pay')} className="landing-nav-link">Pay</button>
              <button onClick={() => setShowZNSModal(true)} className="landing-nav-link">ZNS</button>
              <button onClick={() => navigate('/profile')} className="landing-nav-link">Profile</button>
              <button onClick={() => window.open('/docs', '_blank')} className="landing-nav-link">Docs</button>
            </div>
            <div className="landing-right-section">
              {isConnected && address ? (
                <>
                  <button
                    className="landing-wallet-badge"
                    onClick={() => disconnect()}
                    title="Click to disconnect wallet"
                  >
                    <ZNSAddressDisplay address={address} truncateLength={4} showIcon={true} />
                  </button>
                  <img
                    src={getCurrentChainLogo()}
                    alt="Current Chain"
                    className="landing-chain-logo"
                  />
                </>
              ) : (
                <WalletConnect />
              )}
            </div>
            <button
              className="landing-hamburger"
              onClick={() => setMobileMenuOpen(true)}
            >
              ☰
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`landing-mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div className={`landing-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="landing-mobile-menu-header">
          <img
            src="/landing-assets/mavrk_word_logo.png"
            alt="Mavrk"
            className="landing-mobile-menu-logo"
          />
          <button
            className="landing-mobile-close"
            onClick={() => setMobileMenuOpen(false)}
          >
            ×
          </button>
        </div>

        <div className="landing-mobile-nav-links">
          <button
            onClick={() => { navigate('/create'); handleMobileNavClick(); }}
            className="landing-mobile-nav-link"
          >
            Create Token
          </button>
          <button
            onClick={() => { navigate('/explore'); handleMobileNavClick(); }}
            className="landing-mobile-nav-link"
          >
            Explore
          </button>
          <button
            onClick={() => { navigate('/bridge'); handleMobileNavClick(); }}
            className="landing-mobile-nav-link"
          >
            Bridge
          </button>
          <button
            onClick={() => { navigate('/swap'); handleMobileNavClick(); }}
            className="landing-mobile-nav-link"
          >
            Swap
          </button>
          <button
            onClick={() => { navigate('/locks'); handleMobileNavClick(); }}
            className="landing-mobile-nav-link"
          >
            Locks & TVL
          </button>
          <button
            onClick={() => { navigate('/tydro'); handleMobileNavClick(); }}
            className="landing-mobile-nav-link"
          >
            Tydro
          </button>
          <button
            onClick={() => { navigate('/pay'); handleMobileNavClick(); }}
            className="landing-mobile-nav-link"
          >
            Pay
          </button>
          <button
            onClick={() => { setShowZNSModal(true); handleMobileNavClick(); }}
            className="landing-mobile-nav-link"
          >
            ZNS
          </button>
          <button
            onClick={() => { navigate('/profile'); handleMobileNavClick(); }}
            className="landing-mobile-nav-link"
          >
            Profile
          </button>
          <button
            onClick={() => { window.open('/docs', '_blank'); handleMobileNavClick(); }}
            className="landing-mobile-nav-link"
          >
            Docs
          </button>
        </div>

        <div className="landing-mobile-wallet-section">
          {isConnected && address ? (
            <button
              className="landing-wallet-badge"
              onClick={() => { disconnect(); handleMobileNavClick(); }}
              title="Click to disconnect wallet"
              style={{ width: '100%', marginBottom: '12px' }}
            >
              <ZNSAddressDisplay address={address} truncateLength={4} showIcon={true} />
            </button>
          ) : (
            <WalletConnect />
          )}
        </div>
      </div>

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <h1 className="landing-hero-title">
            Capital-Free Market Formation
          </h1>
          <p className="landing-hero-subtitle">
            Mavrk is the missing primitive for market formation. <br />
            Liquidity as a computational process, not a capital problem.
          </p>



          <div className="landing-cta-buttons" style={{ marginTop: '40px' }}>
            <div style={{ display: 'flex', gap: '20px', marginTop: '16px', justifyContent: 'center' }}>
              <button
                className="landing-nav-link"
                onClick={() => navigate('/create')}
                style={{ fontSize: '1rem', padding: '12px 24px', background: 'rgba(255,255,255,0.05)' }}
              >
                Create Token
              </button>
              <button className="landing-nav-link" onClick={handleOpenApp} style={{ fontSize: '1rem', padding: '12px 24px', background: 'rgba(255,255,255,0.05)' }}>
                Launch App
              </button>
            </div>
          </div>
        </div>

        {(marketStats?.total !== undefined) && (
          <div style={{
            marginTop: '60px',
            display: 'flex',
            gap: '60px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            opacity: 0,
            animation: 'fadeInUp 1s ease-out 1.3s forwards',
            position: 'relative',
            zIndex: 10
          }}>
            <div className="landing-stat-item" style={{
              textAlign: 'center',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontWeight: 300,
              position: 'relative',
              cursor: 'help'
            }}>
              <div style={{
                fontSize: '2.5rem',
                fontWeight: 700,
                color: '#bc13fe',
                textShadow: '0 0 20px rgba(188, 19, 254, 0.4)',
                marginBottom: '8px',
                lineHeight: 1
              }}>{marketStats?.total?.toLocaleString() || '0'}</div>
              <div style={{
                fontSize: '0.9rem',
                color: 'rgba(255, 255, 255, 0.6)',
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                fontWeight: 600
              }}>Markets Formed</div>

              {/* Hover Tooltip for Markets */}
              <div className="landing-stat-tooltip">
                <div className="landing-stat-row">
                  <span>Protofire:</span>
                  <span style={{ color: '#bc13fe' }}>{marketStats?.protofire?.toLocaleString() || '0'}</span>
                </div>
                <div className="landing-stat-row">
                  <span>InkySwap:</span>
                  <span style={{ color: '#00f3ff' }}>{marketStats?.inkyswap?.toLocaleString() || '0'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Problems Section */}
      <section ref={problemsSectionRef} data-section="problems" className={`landing-problems ${visibleSections.has('problems') ? 'visible' : ''}`}>
        <div className="landing-container">
          <h2 className="landing-problems-title">THE PROBLEM</h2>
          <p className="landing-section-subtitle" style={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)', marginBottom: '40px', fontSize: '1.2rem' }}>Capital as a Barrier to Market Formation</p>
          <div className="landing-problems-grid">
            <div className={`landing-problem-card ${visibleSections.has('problems') ? 'visible' : ''}`} style={{ transitionDelay: '0.1s' }}>
              <h3 className="landing-problem-title">Capital Requirements</h3>
              <img src="/landing-assets/capital_bottlenecks_icon.png" alt="Capital Requirements" className="landing-problem-icon" />
              <p className="landing-problem-description">Launchpads demand large liquidity. Presales favor insiders. Creating liquidity pools (LPs) needs capital and expertise.</p>
            </div>
            <div className={`landing-problem-card ${visibleSections.has('problems') ? 'visible' : ''}`} style={{ transitionDelay: '0.2s' }}>
              <h3 className="landing-problem-title">Extractive Mechanisms</h3>
              <img src="/landing-assets/extractive_icon.png" alt="Extractive Mechanisms" className="landing-problem-icon" />
              <p className="landing-problem-description">Bonding curves and presales extract value from communities before markets even form. Hidden fees, unfair mechanics, and rent-seeking intermediaries dominate the landscape.</p>
            </div>
            <div className={`landing-problem-card ${visibleSections.has('problems') ? 'visible' : ''}`} style={{ transitionDelay: '0.3s' }}>
              <h3 className="landing-problem-title">Systemic Fragility</h3>
              <img src="/landing-assets/clones_icon.png" alt="Systemic Fragility" className="landing-problem-icon" />
              <p className="landing-problem-description">Liquidity is inconsistent and vulnerable to rug-pull vectors. Launch mechanisms are non-standardized, and quality control relies on gatekeepers rather than infrastructure.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section (System Architecture) */}
      <section ref={solutionsSectionRef} data-section="architecture" className={`landing-architecture ${visibleSections.has('architecture') ? 'visible' : ''}`}>
        <div className="landing-container">
          <h2 className="landing-architecture-title">The Solution</h2>
          <p className="landing-section-subtitle" style={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)', marginBottom: '60px', fontSize: '1.2rem' }}>Mavrk solves these problems with Autonomous Market Formation Infrastructure</p>

          <div style={{ marginBottom: '80px' }}>
            <SystemArchitecture />
          </div>

          <div className="landing-promise-section" style={{ textAlign: 'center' }}>
            <h3 className="landing-promise-title" style={{ fontSize: '1.5rem', color: '#fff', marginBottom: '30px' }}>The Promise</h3>
            <div className="landing-promise-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', maxWidth: '800px', margin: '0 auto' }}>
              <div className="landing-promise-item" style={{ color: '#bc13fe', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                No presales
              </div>
              <div className="landing-promise-item" style={{ color: '#bc13fe', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                No bonding curves
              </div>
              <div className="landing-promise-item" style={{ color: '#bc13fe', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                No capital
              </div>
              <div className="landing-promise-item" style={{ color: '#00f3ff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                No manual steps
              </div>
              <div className="landing-promise-item" style={{ color: '#00f3ff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                No intermediaries
              </div>
              <div className="landing-promise-item" style={{ color: '#00f3ff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                Just infrastructure
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Breakthrough Section */}
      <section className={`landing-breakthrough ${visibleSections.has('architecture') ? 'visible' : ''}`} style={{ padding: '80px 0', position: 'relative' }}>
        <div className="landing-container">
          <h2 className="landing-architecture-title">The Breakthrough</h2>
          <h3 className="landing-section-subtitle" style={{ textAlign: 'center', color: '#bc13fe', marginBottom: '20px', fontSize: '1.8rem', fontWeight: '700' }}>Algorithmic Liquidity Synthesis</h3>
          <p className="landing-section-subtitle" style={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)', marginBottom: '60px', fontSize: '1.2rem' }}>Liquidity can be algorithmically synthesized. It does not require external capital.</p>

          <div className="landing-breakthrough-content" style={{ display: 'flex', gap: '60px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="landing-breakthrough-chart" style={{ flex: 1, minWidth: '300px' }}>
              <PriceDiscoveryChart />
              <p style={{ textAlign: 'center', marginTop: '20px', color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>Constant Product Formula Price Discovery </p>
            </div>

            <div className="landing-breakthrough-steps" style={{ flex: 1, minWidth: '300px' }}>
              <h3 style={{ color: '#fff', marginBottom: '30px' }}>How It Works:</h3>
              <div className="landing-step-item" style={{ marginBottom: '20px', padding: '20px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ color: '#bc13fe', fontWeight: 'bold', marginBottom: '8px' }}>1. Deterministic Price Initialization</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>Target starting valuation</div>
              </div>
              <div className="landing-step-item" style={{ marginBottom: '20px', padding: '20px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ color: '#bc13fe', fontWeight: 'bold', marginBottom: '8px' }}>2. Concentrated Range Selection</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>Liquidity focused in specific range</div>
              </div>
              <div className="landing-step-item" style={{ marginBottom: '20px', padding: '20px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ color: '#00f3ff', fontWeight: 'bold', marginBottom: '8px' }}>3. Single-Sided Provision</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>Only token required, no ETH pairing</div>
              </div>
              <div className="landing-step-item" style={{ marginBottom: '20px', padding: '20px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ color: '#00f3ff', fontWeight: 'bold', marginBottom: '8px' }}>4. Functional Equivalence</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>Behaves like traditionally funded pool</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresSectionRef} data-section="features" className={`landing-features ${visibleSections.has('features') ? 'visible' : ''}`}>
        <div className="landing-container">
          <h2 className="landing-features-title">Platform Features</h2>
          <div className="landing-features-grid">
            <div className={`landing-feature-card ${visibleSections.has('features') ? 'visible' : ''}`} style={{ transitionDelay: '0.1s' }}>
              <h3 className="landing-feature-title">Instant Token Creation</h3>
              <p className="landing-feature-description">Deploy tokens in seconds with our streamlined creation process. No coding required, just point and click.</p>
              <a href="/create" className="landing-feature-cta">Create Token →</a>
            </div>
            <div className={`landing-feature-card ${visibleSections.has('features') ? 'visible' : ''}`} style={{ transitionDelay: '0.2s' }}>
              <h3 className="landing-feature-title">ImmediateTrading</h3>
              <p className="landing-feature-description">Tokens launch with instant liquidity pools. No manual LP creation or migration needed.</p>
              <a href="/explore" className="landing-feature-cta">Explore Tokens →</a>
            </div>
            <div className={`landing-feature-card ${visibleSections.has('features') ? 'visible' : ''}`} style={{ transitionDelay: '0.3s' }}>
              <h3 className="landing-feature-title">Token Locks & Vesting</h3>
              <p className="landing-feature-description">Secure token locking and vesting schedules. Build trust with transparent token distribution.</p>
              <a href="/locks" className="landing-feature-cta">View Locks →</a>
            </div>
            <div className={`landing-feature-card ${visibleSections.has('features') ? 'visible' : ''}`} style={{ transitionDelay: '0.4s' }}>
              <h3 className="landing-feature-title">Bridge & Swap</h3>
              <p className="landing-feature-description">Seamless cross-chain bridging and token swapping. Connect to multiple networks effortlessly.</p>
              <a href="/swap" className="landing-feature-cta">Start Trading →</a>
            </div>
          </div>
        </div>
      </section>

      {/* Live Activity Section */}
      <section ref={statsSectionRef} data-section="stats" className={`landing-stats ${visibleSections.has('stats') ? 'visible' : ''}`}>
        <div className="landing-container">
          <h2 className="landing-stats-title">Live Activity</h2>
          <RecentDeployments />
        </div>
      </section>

      {/* Token Locks Section */}
      <section ref={locksSectionRef} data-section="locks" className={`landing-locks ${visibleSections.has('locks') ? 'visible' : ''}`}>
        <div className="landing-container">
          <div className="landing-locks-content">
            <div className="landing-locks-text">
              <h2 className="landing-locks-title">Token Locks & Vesting</h2>
              <p className="landing-locks-description">
                Secure your project's future with our advanced token locking system. Create trust through transparent vesting schedules and community-locked assets.
              </p>
              <ul className="landing-locks-features">
                <li>Community-driven token locks</li>
                <li>Flexible vesting schedules</li>
                <li>Transparent lock tracking</li>
                <li>Real-time TVL monitoring</li>
              </ul>
            </div>
            <div className="landing-locks-visual">
              <h3 style={{ color: '#ffffff', marginBottom: '10px' }}>Secure & Transparent</h3>
              <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Build community trust with verifiable token locks</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bridge & Swap Section */}
      <section ref={swapSectionRef} data-section="swap" className={`landing-swap ${visibleSections.has('swap') ? 'visible' : ''}`}>
        <div className="landing-container">
          <div className="landing-swap-content">
            <div className="landing-swap-visual">
              <h3 style={{ color: '#ffffff', marginBottom: '10px' }}>Cross-Chain Ready</h3>
              <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Seamless bridging and swapping across networks</p>
            </div>
            <div className="landing-swap-text">
              <h2 className="landing-swap-title">Bridge & Swap</h2>
              <p className="landing-swap-description">
                Connect to multiple blockchains with our integrated bridge and swap functionality. Trade tokens across networks with minimal fees and maximum security.
              </p>
              <ul className="landing-swap-features">
                <li>Multi-chain token bridging</li>
                <li>Optimized swap routes</li>
                <li>Low-cost transactions</li>
                <li>Real-time price tracking</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Built On Section - back to footer */}
      <section className="landing-built-on">
        <div className="landing-container">
          <p className="landing-built-on-text">built on</p>
          <div className="landing-built-on-logos">
            <img src="/landing-assets/OP_superchain.png" alt="OP Superchain" />
            <span className="landing-plus-icon">+</span>
            <img src="/landing-assets/Ink.png" alt="Ink" />
            <span className="landing-plus-icon">+</span>
            <img src="/landing-assets/ethereum.png" alt="Ethereum" />
          </div>
        </div>
      </section>

      {/* ZNS Registration Modal */}
      <ZNSRegistrationModal
        isVisible={showZNSModal}
        onClose={() => setShowZNSModal(false)}
      />
    </div >
  );
};

export default LandingPage;
